import React, { useState, KeyboardEvent } from 'react'
import { useSelector } from 'react-redux'
import { selectIsBreakpoint } from '../../services/app/selectors'
import { SeedQuestionCategory } from '../../graphql-types'
import { TarotCategoryModal } from './TarotCategoryModal'
import { SendMessage } from '../OracleCommons/icons/SendMessage'
import { useOracleImage } from '../OracleCommons/useOracleImage'
import { StarAi } from '../OracleCommons/icons/StarAi'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { makeIcon } from '../OracleCommons/icons'
import { CollapsiblePanel } from '../OracleCommons/CollapsiblePanel'
import { SegmentedButton, SegmentSpec } from '../OracleCommons/SegmentedButton'
import { buildTarotPullMessage } from './hooks/seedQuestionTagUtils'
import { useTarotSeedQuestions } from './hooks/useTarotSeedQuestions'
import { useAllTarotCategoryQuestions } from './hooks/useAllTarotCategoryQuestions'
import type { TarotSeedCategory } from './hooks/useAllTarotCategoryQuestions'

const CATEGORIES = [
  { key: 'love_relationships' as const },
  { key: 'work_money' as const },
  { key: 'personal_development' as const },
  { key: 'general' as const },
]

export type TarotEntryCategory = typeof CATEGORIES[number]['key']

const SEED_CATEGORY_MAP: Record<TarotEntryCategory, TarotSeedCategory> = {
  love_relationships: SeedQuestionCategory.LoveRelationships,
  work_money: SeedQuestionCategory.WorkMoney,
  personal_development: SeedQuestionCategory.PersonalDevelopment,
  general: SeedQuestionCategory.General,
}

interface TarotEntryScreenProps {
  onBack: () => void
  onSubmit: (message: string) => void
  initialQuestion?: string
}

export const TarotEntryScreen: React.FC<TarotEntryScreenProps> = ({
  onBack,
  onSubmit,
  initialQuestion,
}) => {
  const [question, setQuestion] = useState(initialQuestion ?? '')
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(!initialQuestion)
  const [selectedCategory, setSelectedCategory] = useState<TarotEntryCategory | null>(null)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [desktopSelectedIdx, setDesktopSelectedIdx] = useState(0)

  const isBelowDesktopBreakpoint = useSelector(selectIsBreakpoint(768))

  const { questionsByCategory, loading: categoryQuestionsLoading } = useAllTarotCategoryQuestions()

  const tarotKingWands = useOracleImage('tarot_king_wands.png')
  const tarotStrength = useOracleImage('tarot_strength.png')
  const tarotQueenCups = useOracleImage('tarot_queen_cups.png')
  const tarotStepNumber1 = useOracleImage('tarot-step-1.svg')
  const tarotStepNumber2 = useOracleImage('tarot-step-2.svg')
  const tarotStepNumber3 = useOracleImage('tarot-step-3.svg')

  useTarotSeedQuestions({ skip: false })

  const aiGuideMenuLabel = useTranslatedText({ keys: ['tarotEntryScreen', 'aiGuideMenu'] })
  const howItWorksHeading = useTranslatedText({ keys: ['tarotEntryScreen', 'howItWorksHeading'] })
  const step1Title = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step1Title'] })
  const step1Desc = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step1Desc'] })
  const step2Title = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step2Title'] })
  const step2Desc = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step2Desc'] })
  const step3Title = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step3Title'] })
  const step3Desc = useTranslatedText({ keys: ['tarotEntryScreen', 'steps', 'step3Desc'] })
  const inputPlaceholder = useTranslatedText({ keys: ['tarotEntryScreen', 'inputPlaceholder'] })
  const categoriesLabel = useTranslatedText({ keys: ['tarotEntryScreen', 'categoriesLabel'] })
  const categoryLoveRelationships = useTranslatedText({ keys: ['tarotEntryScreen', 'categories', 'loveRelationships'] })
  const categoryWorkMoney = useTranslatedText({ keys: ['tarotEntryScreen', 'categories', 'workMoney'] })
  const categoryPersonalGrowth = useTranslatedText({ keys: ['tarotEntryScreen', 'categories', 'personalGrowth'] })
  const categoryIDontKnow = useTranslatedText({ keys: ['tarotEntryScreen', 'categories', 'iDontKnow'] })
  const sendAriaLabel = useTranslatedText({ keys: ['tarotEntryScreen', 'sendAriaLabel'] })

  const handleSubmit = () => {
    const trimmed = question.trim()
    if (!trimmed) return
    onSubmit(buildTarotPullMessage(trimmed))
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const steps = [
    { title: step1Title, desc: step1Desc, numberSrc: tarotStepNumber1 },
    { title: step2Title, desc: step2Desc, numberSrc: tarotStepNumber2 },
    { title: step3Title, desc: step3Desc, numberSrc: tarotStepNumber3 },
  ]

  const categoryLabels: Record<TarotEntryCategory, string> = {
    love_relationships: categoryLoveRelationships,
    work_money: categoryWorkMoney,
    personal_development: categoryPersonalGrowth,
    general: categoryIDontKnow,
  }

  const leftColumnCategories = CATEGORIES.filter((_, i) => i % 2 === 0)
  const rightColumnCategories = CATEGORIES.filter((_, i) => i % 2 === 1)

  const handleQuestionSelect = (selectedQuestion: string) => {
    onSubmit(buildTarotPullMessage(selectedQuestion, { forceAccept: true }))
  }

  const desktopCategoryKey = CATEGORIES[desktopSelectedIdx].key
  const desktopQuestions = questionsByCategory[SEED_CATEGORY_MAP[desktopCategoryKey]] ?? []

  const desktopSegments: SegmentSpec[] = CATEGORIES.map(({ key }, idx) => ({
    text: categoryLabels[key],
    onSelect: () => setDesktopSelectedIdx(idx),
  }))

  return (
    <div className="tarot-entry-screen">
      <div className="tarot-entry-screen__top-bar">
        <button
          type="button"
          className="tarot-entry-screen__ai-guide-menu-btn"
          onClick={onBack}
          aria-label={aiGuideMenuLabel}
        >
          <span className="tarot-entry-screen__ai-guide-menu-btn-icon">
            {makeIcon('panel')}
          </span>
          <span className="tarot-entry-screen__ai-guide-menu-btn-text">
            {aiGuideMenuLabel}
          </span>
        </button>
      </div>
      <div className="tarot-entry-screen__content">
        <div className="tarot-entry-screen__hero">
          <div className="tarot-entry-screen__hero-heading">
            <h1 className="tarot-entry-screen__title">Tarot</h1>
            <div className="tarot-entry-screen__card-fan" aria-hidden="true">
              <img className="tarot-entry-screen__card tarot-entry-screen__card--left" src={tarotQueenCups} alt="" />
              <img className="tarot-entry-screen__card tarot-entry-screen__card--center" src={tarotStrength} alt="" />
              <img className="tarot-entry-screen__card tarot-entry-screen__card--right" src={tarotKingWands} alt="" />
            </div>
          </div>
          <div className="tarot-entry-screen__divider" aria-hidden="true">
            <div className="tarot-entry-screen__divider-line" />
            <StarAi size="16px" />
            <div className="tarot-entry-screen__divider-line tarot-entry-screen__divider-line--flipped" />
          </div>
        </div>

        <CollapsiblePanel
          label={howItWorksHeading}
          isOpen={isHowItWorksOpen}
          onChange={setIsHowItWorksOpen}
          className="tarot-entry-screen__how-it-works"
          content={(
            <div className="tarot-entry-screen__steps">
              {steps.map(({ title, desc, numberSrc }, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="tarot-entry-screen__step">
                  <img
                    className="tarot-entry-screen__step-number"
                    src={numberSrc}
                    aria-hidden="true"
                    alt=""
                  />
                  <div className="tarot-entry-screen__step-content">
                    <span className="tarot-entry-screen__step-title">{title}</span>
                    <p className="tarot-entry-screen__step-desc">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        />

        <div className="tarot-entry-screen__input-area">
          <div className="tarot-entry-screen__input-box">
            <textarea
              className="tarot-entry-screen__input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsHowItWorksOpen(false)}
              placeholder={inputPlaceholder}
              rows={1}
            />
            <div className="tarot-entry-screen__input-actions">
              <button
                type="button"
                className="tarot-entry-screen__send-btn"
                onClick={handleSubmit}
                disabled={!question.trim()}
                aria-label={sendAriaLabel}
              >
                <SendMessage size="18px" color="white" />
              </button>
            </div>
          </div>
        </div>

        <div className="tarot-entry-screen__categories">
          <p className="tarot-entry-screen__categories-label">{categoriesLabel}</p>
          {isBelowDesktopBreakpoint ? (
            <div className="tarot-entry-screen__categories-grid">
              <div className="tarot-entry-screen__categories-col">
                {leftColumnCategories.map(({ key }) => (
                  <button
                    key={key}
                    type="button"
                    className="tarot-entry-screen__category-btn"
                    onClick={() => { setSelectedCategory(key); setIsCategoryModalOpen(true) }}
                  >
                    {categoryLabels[key]}
                  </button>
                ))}
              </div>
              <div className="tarot-entry-screen__categories-col">
                {rightColumnCategories.map(({ key }) => (
                  <button
                    key={key}
                    type="button"
                    className="tarot-entry-screen__category-btn"
                    onClick={() => { setSelectedCategory(key); setIsCategoryModalOpen(true) }}
                  >
                    {categoryLabels[key]}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <SegmentedButton
                allowedSelection="single"
                segments={desktopSegments}
                selectedIdx={desktopSelectedIdx}
                ariaLabel={categoriesLabel}
              />
              <div className="tarot-entry-screen__desktop-questions">
                {desktopQuestions.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    className="tarot-entry-screen__desktop-question-btn"
                    onClick={() => handleQuestionSelect(q.question)}
                  >
                    <span className="tarot-entry-screen__desktop-question-text">{q.question}</span>
                    <span className="tarot-entry-screen__desktop-question-send" aria-hidden="true">
                      {makeIcon('send-message', '14px')}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {isBelowDesktopBreakpoint && (
        <TarotCategoryModal
          category={selectedCategory}
          categoryLabel={selectedCategory ? categoryLabels[selectedCategory] : ''}
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onSelectQuestion={handleQuestionSelect}
          questions={
            selectedCategory ? questionsByCategory[SEED_CATEGORY_MAP[selectedCategory]] : []
          }
          loading={categoryQuestionsLoading}
        />
      )}
    </div>
  )
}
